import { useMemo } from 'react';
import { useList } from '@refinedev/core';
import { Copy, Globe, Bot, AppWindow } from 'lucide-react';
import { notify } from '@/lib/notify';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import type { UtmSourceItem } from './types';

interface TelegramBotOption {
  id: number;
  name: string;
  username: string | null;
  isActive: boolean;
}

interface UtmCopyLinkDropdownProps {
  source: UtmSourceItem;
}

/**
 * Кнопка-копировалка UTM-ссылки с dropdown-меню каналов:
 *   - Платформа: {PLATFORM_BASE_URL}/?utm_source=<name>
 *   - Telegram-бот (per-bot): t.me/<bot>?start=u_<shortCode>
 *   - Telegram Mini App (per-bot): t.me/<bot>?startapp=u_<shortCode>
 *
 * Паттерн копирует UX из user-invitations/list.tsx.
 */
export function UtmCopyLinkDropdown({ source }: UtmCopyLinkDropdownProps) {
  const botsResult = useList<TelegramBotOption>({
    resource: 'bot-tokens',
    pagination: { pageSize: 200, mode: 'server' },
    queryOptions: { staleTime: 60_000 },
  });

  const activeBots = useMemo<TelegramBotOption[]>(
    () => (botsResult.query.data?.data ?? []).filter((b) => b.isActive && b.username),
    [botsResult.query.data],
  );

  const platformLink = source.botLink ?? source.links?.platform ?? null;

  const copyToClipboard = (text: string, successMessage: string) => {
    void navigator.clipboard.writeText(text).then(
      () => notify.success(successMessage),
      () => notify.error('Не удалось скопировать'),
    );
  };

  const buildBotUrl = (botUsername: string, shortCode: string): string =>
    `https://t.me/${botUsername}?start=u_${shortCode}`;

  const buildMiniAppUrl = (botUsername: string, shortCode: string): string =>
    `https://t.me/${botUsername}?startapp=u_${shortCode}`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          title="Скопировать UTM ссылку"
          onClick={(e) => e.stopPropagation()}
        >
          <Copy className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuLabel>Скопировать UTM ссылку</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={!platformLink}
          onSelect={() => {
            if (!platformLink) {
              notify.error('Ссылка на платформу недоступна');
              return;
            }
            copyToClipboard(platformLink, 'Ссылка на платформу скопирована');
          }}
        >
          <Globe className="h-4 w-4 mr-2" />
          <div className="flex flex-col">
            <span>Платформа</span>
            <span className="text-xs text-muted-foreground">?utm_source=…</span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Telegram-бот
        </DropdownMenuLabel>
        {botsResult.query.isLoading ? (
          <DropdownMenuItem disabled>Загрузка ботов…</DropdownMenuItem>
        ) : activeBots.length === 0 ? (
          <DropdownMenuItem disabled>Нет активных ботов</DropdownMenuItem>
        ) : (
          activeBots.map((bot) => (
            <DropdownMenuItem
              key={`bot-${bot.id}`}
              onSelect={() => {
                if (!bot.username) { return; }
                copyToClipboard(
                  buildBotUrl(bot.username, source.shortCode),
                  `Ссылка на @${bot.username} скопирована`,
                );
              }}
            >
              <Bot className="h-4 w-4 mr-2" />
              <div className="flex flex-col min-w-0">
                <span className="truncate">{bot.name}</span>
                <span className="text-xs text-muted-foreground truncate">
                  @{bot.username} · /start
                </span>
              </div>
            </DropdownMenuItem>
          ))
        )}

        {activeBots.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
              Telegram Mini App
            </DropdownMenuLabel>
            {activeBots.map((bot) => (
              <DropdownMenuItem
                key={`miniapp-${bot.id}`}
                onSelect={() => {
                  if (!bot.username) { return; }
                  copyToClipboard(
                    buildMiniAppUrl(bot.username, source.shortCode),
                    `Mini App ссылка на @${bot.username} скопирована`,
                  );
                }}
              >
                <AppWindow className="h-4 w-4 mr-2" />
                <div className="flex flex-col min-w-0">
                  <span className="truncate">{bot.name}</span>
                  <span className="text-xs text-muted-foreground truncate">
                    @{bot.username} · startapp
                  </span>
                </div>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
